/**
 * @file useWarehouseLabelSheets.ts
 * @description Данные вкладки «Этикетки» настроек склада (phase19.22,
 *   W17-5a/5b; ui-design §3): листы самоклейки с калибровкой, принтеры
 *   этикеток склада, принтер по умолчанию, сохранение и пробная страница.
 *
 * Сохранение — два запроса по двум ресурсам (калибровка листов и настройки
 * склада); принтер шлём, только если он поменялся. После сохранения форма
 * перечитывается с сервера — источник правды там.
 */

import { useCallback, useEffect, useState } from "react";

import { draftsFrom, fromDraft, type MarginDraft } from "./label-sheet-form";
import { useNotify } from "../../ui/NotifyProvider";
import {
  labelsApi,
  type LabelSheetMargins,
  type LabelSheetsDto,
  type LabelTemplate,
} from "../../../lib/api-labels";
import { devicesApi, warehousesApi, type DeviceConfigDto } from "../../../lib/api-wms";
import { saveBlob } from "../../../lib/download";
import { wmsErrorText } from "../../../lib/wms-error-text";

export function useWarehouseLabelSheets(warehouseId: string) {
  const notify = useNotify();
  const [data, setData] = useState<LabelSheetsDto | null>(null);
  const [printers, setPrinters] = useState<DeviceConfigDto[]>([]);
  const [drafts, setDrafts] = useState<Partial<Record<LabelTemplate, MarginDraft>>>({});
  const [printerId, setPrinterId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sheets, devices] = await Promise.all([labelsApi.labelSheets(warehouseId), devicesApi.list(warehouseId)]);
      setData(sheets);
      setDrafts(draftsFrom(sheets.sheets));
      setPrinterId(sheets.defaultLabelPrinterId ?? "");
      setPrinters(devices.filter((d) => d.kind === "PRINTER_LABEL" && d.isActive));
      setError(null);
    } catch (err) {
      setError(wmsErrorText(err, "Не удалось загрузить настройки этикеток"));
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const valid = !!data && data.sheets.every((s) => drafts[s.template] && fromDraft(drafts[s.template]!));

  async function save() {
    if (!data || !valid || saving) return;
    setSaving(true);
    try {
      const sheets = data.sheets.map((s) => ({ template: s.template, ...(fromDraft(drafts[s.template]!) as LabelSheetMargins) }));
      await labelsApi.saveLabelSheets(warehouseId, sheets);
      if ((data.defaultLabelPrinterId ?? "") !== printerId) {
        await warehousesApi.updateSettings(warehouseId, { defaultLabelPrinterId: printerId || null });
      }
      notify.success("Настройки этикеток сохранены");
      await load();
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось сохранить настройки этикеток"));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Пробная страница по текущим (несохранённым) значениям. Вкладку открываем
   * СИНХРОННО по клику (до запроса): окно, открытое после `await`, Safari/iOS
   * блокирует. PDF пришёл — ведём вкладку на него; окно не открылось —
   * скачиваем файл; сбой — закрываем пустую вкладку.
   */
  async function calibrate(template: LabelTemplate) {
    const margins = drafts[template] ? fromDraft(drafts[template]!) : null;
    const win = window.open("", "_blank");
    try {
      const file = await labelsApi.calibrationPdf(warehouseId, template, margins ?? undefined);
      const url = URL.createObjectURL(file.blob);
      if (win) win.location.href = url;
      else saveBlob(file.blob, file.filename);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      win?.close();
      notify.error(wmsErrorText(err, "Сервис печати недоступен — пробная страница не сформирована"));
    }
  }

  return {
    data,
    printers,
    drafts,
    setDraft: (template: LabelTemplate, draft: MarginDraft) => setDrafts((prev) => ({ ...prev, [template]: draft })),
    printerId,
    setPrinterId,
    loading,
    error,
    saving,
    valid,
    save,
    calibrate,
    reload: load,
  };
}
